import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { formatAlertHour } from '../../config/spendAlertIntents';
import { useAuth } from '../../contexts/AuthContext';
import { navigateToAuth } from '../../core/navigation/navigationRef';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { useBillingActions } from '../../hooks/useBillingActions';
import { formatCardTitle, formatDueDate } from './creditCardFormat';
import {
  AnalyzeQuotaModal,
  type AnalyzeQuotaInfo,
} from '../billing/AnalyzeQuotaModal';
import { GlassButton } from '../ui/GlassButton';
import {
  listSpendAlerts,
  type ChannelReadiness,
  type SpendAlert,
} from '../../services/spendAlertService';
import { subscribeSpendAlertsChanged } from '../../services/spendAlertsBus';
import type { CardSpendRow } from '../../types/analytics';
import { SetSpendAlertModal, type SpendAlertModalSeed } from './SetSpendAlertModal';

export type SpendAlertCatalog = {
  alerts: SpendAlert[];
  channels: ChannelReadiness | null;
  reload: () => Promise<void>;
};

type Props = {
  card: CardSpendRow;
  /** Shared list from the Cards widget so every row does not refetch. */
  catalog?: SpendAlertCatalog;
  /** Tighter label for the Cards list (no timing chip). */
  compact?: boolean;
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function cardDueAlertSlug(spendAccountId: string): string {
  return `card:${spendAccountId}`;
}

export function findCardDueAlert(
  alerts: SpendAlert[],
  card: CardSpendRow,
): SpendAlert | undefined {
  const slug = cardDueAlertSlug(card.spendAccountId);
  const byId = alerts.find((a) => a.categorySlug === slug);
  if (byId) return byId;
  const title = formatCardTitle(card);
  return alerts.find(
    (a) =>
      (a.categorySlug === 'card' || a.categorySlug.startsWith('card:')) &&
      a.categoryLabel === title,
  );
}

function dueWeekday(iso?: string | null): number {
  if (!iso) return new Date().getDay();
  const date = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return new Date().getDay();
  return date.getUTCDay();
}

function seedFromCard(card: CardSpendRow): SpendAlertModalSeed {
  const due = formatDueDate(card.paymentDueDate);
  const title = formatCardTitle(card);
  const dueBit = due ? ` due ${due}` : '';
  const amount =
    card.statementBalance != null
      ? ` Pay-in-full is $${card.statementBalance.toFixed(2)}.`
      : '';
  return {
    categorySlug: cardDueAlertSlug(card.spendAccountId),
    categoryLabel: title,
    intent: 'general',
    dayOfWeek: dueWeekday(card.paymentDueDate),
    hourLocal: 9,
    leadHours: 3,
    avgAmount: card.statementBalance ?? card.minimumPayment ?? card.totalSpend,
    reason: `Remind me before ${title}${dueBit}.${amount}`,
  };
}

/** Shared alert list for the Cards widget (one fetch, many rows). */
export function useSpendAlertCatalog(): SpendAlertCatalog {
  const { isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState<SpendAlert[]>([]);
  const [channels, setChannels] = useState<ChannelReadiness | null>(null);

  const reload = useCallback(async () => {
    if (!isAuthenticated) {
      setAlerts([]);
      setChannels(null);
      return;
    }
    try {
      const res = await listSpendAlerts();
      setAlerts(res.alerts ?? []);
      setChannels(res.channels ?? null);
    } catch {
      setChannels(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => subscribeSpendAlertsChanged(() => void reload()), [reload]);

  return { alerts, channels, reload };
}

/**
 * Due-date alert control. Visible for everyone; saving is Pro-gated
 * and free users get the upgrade checkout sheet.
 * Signed-out users are sent to sign-in first.
 * Once saved, the same button becomes a manage control (edit via the modal).
 */
export function CardDueAlertButton({ card, catalog, compact = false }: Props) {
  const { user, isAuthenticated } = useAuth();
  const { upgradeToPro, busy: upgradeBusy } = useBillingActions();
  const isPro = user?.plan === 'pro';
  const [modalOpen, setModalOpen] = useState(false);
  const [proGate, setProGate] = useState<AnalyzeQuotaInfo | null>(null);
  const [localChannels, setLocalChannels] = useState<ChannelReadiness | null>(null);
  const [localExisting, setLocalExisting] = useState<SpendAlert | null>(null);
  const [editing, setEditing] = useState<SpendAlert | null>(null);
  const seed = useMemo(() => seedFromCard(card), [card]);

  const loadLocal = useCallback(async () => {
    if (catalog || !isAuthenticated) {
      if (!isAuthenticated) {
        setLocalExisting(null);
        setLocalChannels(null);
      }
      return;
    }
    try {
      const res = await listSpendAlerts();
      setLocalChannels(res.channels ?? null);
      setLocalExisting(findCardDueAlert(res.alerts ?? [], card) ?? null);
    } catch {
      setLocalChannels(null);
    }
  }, [catalog, isAuthenticated, card.spendAccountId, card.label, card.last4]);

  useEffect(() => {
    void loadLocal();
  }, [loadLocal]);

  useEffect(() => {
    if (catalog) return undefined;
    return subscribeSpendAlertsChanged(() => void loadLocal());
  }, [catalog, loadLocal]);

  const existing = catalog
    ? findCardDueAlert(catalog.alerts, card) ?? null
    : localExisting;
  const channels = catalog?.channels ?? localChannels;

  const open = () => {
    if (!isAuthenticated) {
      navigateToAuth();
      return;
    }
    if (!isPro) {
      setProGate({
        title: 'Pro unlocks card due alerts',
        message:
          'Get a reminder before this card is due so you can plan pay-in-full vs minimum — push or email.',
        upgradeRequired: true,
      });
      return;
    }
    setEditing(existing);
    setModalOpen(true);
  };

  const label = existing
    ? existing.enabled
      ? 'Alert on'
      : 'Alert paused'
    : 'Set alert';
  const timing =
    existing != null
      ? `${DOW[existing.dayOfWeek] ?? 'Day'} · ${formatAlertHour(existing.hourLocal)}`
      : null;

  return (
    <View style={styles.wrap}>
      <GlassButton
        variant="secondary"
        size="sm"
        selected={Boolean(existing?.enabled)}
        onPress={open}
        accessibilityLabel={
          existing
            ? `Manage due-date alert for this card${timing ? `, ${timing}` : ''}`
            : isPro
              ? 'Set a due-date alert for this card'
              : 'Set a due-date alert — Pro'
        }
      >
        {label}
      </GlassButton>
      {timing && !compact ? <Text style={styles.timing}>{timing}</Text> : null}
      {!isPro ? <Text style={styles.proChip}>Pro</Text> : null}

      <SetSpendAlertModal
        visible={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        seed={editing ? undefined : seed}
        editing={editing}
        channels={channels}
        onSaved={() => {
          if (catalog) void catalog.reload();
          else void loadLocal();
        }}
      />

      <AnalyzeQuotaModal
        visible={Boolean(proGate)}
        info={proGate}
        onDismiss={() => setProGate(null)}
        upgradeBusy={upgradeBusy}
        source="card_due"
        onUpgrade={() => {
          setProGate(null);
          void upgradeToPro('card_due');
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  timing: {
    ...typography.caption,
    fontSize: 11,
    color: colors.textMuted,
  },
  proChip: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
});
